import _ from 'lodash'
import React, { useMemo } from 'react'
import styled from 'styled-components'

import { CustomerInvoiceFragment } from '../../../../../../graphql/camel'
import { isNotNull } from '../../../../../../utils/collectionTools'
import { formatAsCurrency, formatAsCurrencyOrZero } from '../../../../../../utils/priceTools'
import * as Styled from './styled'

export interface InvoiceTotalProps {
  className?: string
  invoice: CustomerInvoiceFragment
}

export type InvoiceItemEntryData = NonNullable<CustomerInvoiceFragment['items'][number]>

const InvoiceTotal: React.FC<InvoiceTotalProps> = ({ className, invoice }) => {
  const invoiceItems = useMemo(() => {
    return invoice.items?.filter(isNotNull) ?? []
  }, [invoice.items])

  // TODO can refactor to use Ramda (like OrderTotal) after discountAmount is made a required field
  const additionalDiscount = useMemo(() => {
    const sumOfLineDiscounts = invoiceItems
      .map((item) => item.discountAmount)
      .reduce((acc: number, curr) => acc + (curr ?? 0), 0)
    // round to avoid floating point artifacts
    return Math.max(_.round(invoice.discountTotal - sumOfLineDiscounts, 3), 0)
  }, [invoiceItems, invoice.discountTotal])

  return (
    <div className={className}>
      <Styled.InvoiceTotalDetail>
        <span>Subtotal:</span>
        <span>{formatAsCurrencyOrZero({ amount: invoice.subtotal })}</span>
      </Styled.InvoiceTotalDetail>
      <Styled.InvoiceTotalDetail>
        <span>Shipping:</span>
        <span>{formatAsCurrencyOrZero({ amount: invoice.shippingTotal })}</span>
      </Styled.InvoiceTotalDetail>
      <Styled.InvoiceTotalDetail>
        <span>Sales Tax:</span>
        <span>{formatAsCurrencyOrZero({ amount: invoice.taxTotal })}</span>
      </Styled.InvoiceTotalDetail>
      {invoice.coloradoRetailDeliveryFee && (
        <Styled.InvoiceTotalDetail>
          <span>Colorado Retail Delivery Fee:</span>
          <span>{formatAsCurrency({ amount: invoice.coloradoRetailDeliveryFee })}</span>
        </Styled.InvoiceTotalDetail>
      )}
      {additionalDiscount > 0 && additionalDiscount !== invoice.discountTotal && (
        <Styled.InvoiceTotalDetail>
          <span>Additional Discount:</span>
          <span>{formatAsCurrencyOrZero({ amount: additionalDiscount * -1 })}</span>
        </Styled.InvoiceTotalDetail>
      )}
      {invoice.discountTotal > 0 && (
        <div>
          <Styled.InvoiceTotalDetail>
            <span>Total Discount:</span>
            <span>{formatAsCurrencyOrZero({ amount: invoice.discountTotal * -1 })}</span>
          </Styled.InvoiceTotalDetail>
        </div>
      )}
      <div>
        <Styled.InvoiceTotalGrand>
          <span>Invoice Total:</span>
          <span>{formatAsCurrencyOrZero({ amount: invoice.total })}</span>
        </Styled.InvoiceTotalGrand>
        <Styled.InvoiceAmountRemaining>
          <span>Amount Due:</span>
          <span>{formatAsCurrencyOrZero({ amount: invoice.amountRemaining })}</span>
        </Styled.InvoiceAmountRemaining>
      </div>
    </div>
  )
}

const StyledInvoiceTotal = styled(InvoiceTotal)`
  font-size: 15px;
  line-height: 2;
`

export { StyledInvoiceTotal as InvoiceTotal }
